import axios from "axios";
import { logOut, sanitizeClientData } from "./store/redux/client_reducer.js";
import { staffLogOut } from "./store/redux/universeStaffReducer.js";
import { onlogOutTerminalAction } from "./store/redux/terminal_reducer.js";

export const serverHost =
  process.env.REACT_APP_SERVER_SCRIPT_HOST ||
  "http://localhost:5000/api/v1/universe-inventory".trim().replace(/\/+$/, "");

const https = axios.create({
  baseURL: serverHost,
  headers: {
    "Content-Type": "application/json",
  },
});

let sessionStore;
let preserveTerminal = false;
const readClaims = (token) => {
  try {
    if (
      typeof token !== "string" ||
      !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token)
    )
      return null;
    const payload = token.split(".")[0].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(
      decodeURIComponent(
        Array.from(
          atob(payload),
          (char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`,
        ).join(""),
      ),
    );
  } catch {
    return null;
  }
};

// The server verifies the signature and current account. This check controls client navigation only.
export const activeSessionClaims = (token, accessTypes) => {
  const claims = readClaims(token);
  return claims?.v === 1 &&
    Number.isFinite(claims.exp) &&
    claims.exp > Date.now() &&
    accessTypes.includes(claims.accessType)
    ? claims
    : null;
};

export const expireSession = (kind, token) => {
  if (!sessionStore) return;
  const state = sessionStore.getState();
  const current =
    kind === "staff"
      ? state.universeStaffs?.sessionToken
      : state.clientFunction?.sessionToken;
  if (current !== token) return;
  if (kind === "staff") sessionStore.dispatch(staffLogOut());
  else {
    // Retain the basket and its sale ID so a lost checkout confirmation can be retried after signing in.
    preserveTerminal = true;
    try {
      sessionStore.dispatch(logOut());
    } finally {
      preserveTerminal = false;
    }
  }
};

export const configureSessionRequests = (store) => {
  if (sessionStore) return;
  sessionStore = store;
  const apiTarget = (config, includeLogin = false) => {
    try {
      const origin = window.location.origin;
      const host = new URL(serverHost, origin);
      const request = new URL(axios.getUri(config), origin);
      const prefix = host.pathname.replace(/\/+$/, "");
      if (
        request.origin !== host.origin ||
        !request.pathname.startsWith(`${prefix}/`)
      )
        return null;
      const path = request.pathname.slice(prefix.length);
      if (
        !includeLogin &&
        /\/login(?:s)?$|\/staff_logins$|\/authentication\/login$/.test(path)
      )
        return null;
      return /^\/(?:executive-director|sdd|fdc|bdr|universe-staff)\//.test(
        path,
      ) || ["/update", "/postMany", "/notifications/sms/send"].includes(path)
        ? "staff"
        : "client";
    } catch {
      return null;
    }
  };
  const attachSession = (config) => {
    const kind = apiTarget(config);
    if (!kind) return config;
    const state = store.getState();
    const token =
      kind === "staff"
        ? state.universeStaffs?.sessionToken
        : state.clientFunction?.sessionToken;
    const types =
      kind === "staff" ? ["STAFF_ACCESS"] : ["CLIENT_ACCESS", "USER_ACCESS"];
    if (activeSessionClaims(token, types)) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
      config.universeSession = { kind, token };
    }
    return config;
  };
  const rejectSession = (failure) => {
    const session = failure.config?.universeSession;
    if (failure.response?.status === 401 && session)
      expireSession(session.kind, session.token);
    return Promise.reject(failure);
  };
  [axios, https].forEach((client) => {
    client.interceptors.request.use(attachSession);
    client.interceptors.response.use((response) => {
      if (
        apiTarget(response.config || {}, true) &&
        !(typeof Blob !== "undefined" && response.data instanceof Blob) &&
        !(response.data instanceof ArrayBuffer)
      )
        response.data = sanitizeClientData(response.data, true);
      return response;
    }, rejectSession);
  });
  let previousClientToken, previousStaffToken, lastClientIdentity;
  const expiryTimers = {};
  const watchSessions = () => {
    const state = store.getState();
    const clientToken = state.clientFunction?.sessionToken;
    const staffToken = state.universeStaffs?.sessionToken;
    const claims = readClaims(clientToken);
    const identity = claims?.clientId && `${claims.clientId}:${claims.userId}`;
    if (clientToken !== previousClientToken) {
      const oldToken = previousClientToken;
      previousClientToken = clientToken;
      clearTimeout(expiryTimers.client);
      if (
        (identity && lastClientIdentity && identity !== lastClientIdentity) ||
        (!clientToken && oldToken && !preserveTerminal)
      ) {
        store.dispatch(onlogOutTerminalAction());
      }
      if (identity) lastClientIdentity = identity;
      if (claims?.exp)
        expiryTimers.client = setTimeout(
          () => expireSession("client", clientToken),
          Math.max(0, claims.exp - Date.now()),
        );
    }
    if (staffToken !== previousStaffToken) {
      previousStaffToken = staffToken;
      clearTimeout(expiryTimers.staff);
      const staffClaims = readClaims(staffToken);
      if (staffClaims?.exp)
        expiryTimers.staff = setTimeout(
          () => expireSession("staff", staffToken),
          Math.max(0, staffClaims.exp - Date.now()),
        );
    }
  };
  store.subscribe(watchSessions);
  watchSessions();
};

export default https;
